<script>
  import { createEventDispatcher } from 'svelte';
  import { auth } from '../../stores/auth';
  import { demoMode } from '../../stores/demoMode';
  import { isMobile } from '../../stores/screenSize';

  export let active = 'dashboard';
  export let showMobile = false;

  const dispatch = createEventDispatcher();

  const nav = [
    { id: 'dashboard', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { id: 'workOrders', label: 'Work Orders', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
    { id: 'projects', label: 'Projects', icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z' },
    { id: 'clients', label: 'Clients', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
    { id: 'workers', label: 'Workers', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' }
  ];

  function select(id) {
    active = id;
    dispatch('change', id);
    if ($isMobile) showMobile = false;
  }

  function logout() {
    demoMode.disable();
    auth.logout();
    window.location.href = '/';
  }
</script>

{#if $isMobile && showMobile}
  <div class="overlay" on:click={() => showMobile = false} on:keydown={() => {}} role="button" tabindex="-1" />
{/if}

<aside class:open={showMobile || !$isMobile}>
  <header>
    <div class="logo">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
        <path d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    </div>
    <span>WorkFlow</span>
    {#if $demoMode}<span class="demo">Demo</span>{/if}
  </header>

  <nav>
    {#each nav as item}
      <button class:active={active === item.id} on:click={() => select(item.id)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d={item.icon} />
        </svg>
        {item.label}
      </button>
    {/each}
  </nav>

  {#if $auth.user}
    <footer>
      <div class="avatar">{$auth.user.username?.charAt(0).toUpperCase() || 'U'}</div>
      <div class="info">
        <strong>{$auth.user.username || 'User'}</strong>
        <span>Administrator</span>
      </div>
      <button class="logout" on:click={logout} title="Sign out">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
      </button>
    </footer>
  {/if}
</aside>

<style>
  .overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.4);
    z-index: 40;
  }

  aside {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: 220px;
    background: #fff;
    border-right: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    z-index: 50;
    transform: translateX(-100%);
    transition: transform 150ms ease;
  }

  aside.open { transform: translateX(0); }

  header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px;
    border-bottom: 1px solid var(--border);
  }

  .logo {
    width: 28px;
    height: 28px;
    background: var(--primary);
    border-radius: 6px;
    display: grid;
    place-items: center;
    color: #fff;
  }

  .logo svg { width: 16px; height: 16px; }

  header span {
    font-weight: 600;
    font-size: 15px;
    color: var(--text);
  }

  .demo {
    margin-left: auto;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 3px 6px;
    background: var(--primary);
    color: #fff;
    border-radius: 4px;
  }

  nav {
    flex: 1;
    padding: 8px;
    overflow-y: auto;
  }

  nav button {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 12px;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-muted);
    background: transparent;
    border: none;
    border-radius: var(--radius);
    cursor: pointer;
    transition: all 100ms;
    text-align: left;
  }

  nav button:hover {
    background: var(--bg);
    color: var(--text);
  }

  nav button.active {
    background: var(--primary);
    color: #fff;
  }

  nav button svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }

  footer {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border-top: 1px solid var(--border);
    background: var(--bg);
  }

  .avatar {
    width: 32px;
    height: 32px;
    background: var(--primary);
    border-radius: 6px;
    display: grid;
    place-items: center;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
  }

  .info {
    flex: 1;
    min-width: 0;
  }

  .info strong {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .info span {
    font-size: 11px;
    color: var(--text-muted);
  }

  .logout {
    width: 28px;
    height: 28px;
    display: grid;
    place-items: center;
    background: transparent;
    border: none;
    border-radius: var(--radius);
    color: var(--text-muted);
    cursor: pointer;
    transition: all 100ms;
  }

  .logout:hover {
    background: #fee2e2;
    color: #dc2626;
  }

  .logout svg { width: 16px; height: 16px; }

  @media (min-width: 768px) {
    aside { transform: translateX(0); }
  }
</style>
